import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Ticket,
  DollarSign,
  Users,
  HelpCircle,
  MessageSquare,
  ChevronRight,
  X,
  Info,
  ClipboardList,
  Wrench,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Sidebar from '../components/Sidebar';
import { ticketService } from '../services/ticketService';
import { expenseService } from '../services/expenseService';
import { propertyService } from '../services/propertyService';
import { tenantService } from '../services/tenantService';
import { authService } from '../services/authService';
import CreateTenantAccountModal from '../components/CreateTenantAccountModal';
import OnboardingTutorial from '../components/OnboardingTutorial';
import FeedbackModal from '../components/FeedbackModal';

const OwnerDashboard = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [showOnboardingTutorial, setShowOnboardingTutorial] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    type: 'residential'
  });
  const [profileCompletionForm, setProfileCompletionForm] = useState({
    phoneNumber: userData?.phoneNumber || '',
    address: userData?.address || ''
  });

  // Helper function to check if onboarding is needed
  const needsOnboarding = () => {
    return !userData?.onboardingCompleted && !userData?.profileCompletedAt;
  };

  useEffect(() => {
    if (currentUser && userData) {
      console.log('Setting up owner dashboard for:', currentUser.uid);
      console.log('User data:', userData);
      console.log('Needs onboarding:', needsOnboarding());
      try {
        // Check if user needs to complete profile (phone and address)
        if (!userData.phoneNumber || !userData.address) {
          console.log('Profile completion needed - phone or address missing');
          setShowProfileCompletion(true);
          // Complete loading since we're showing profile completion modal
          setLoading(false);
          return; // Return early since we don't need subscriptions for profile completion
        }
        
        // Subscribe to properties
        const propertyUnsubscribe = propertyService.subscribeToPropertiesByOwner(
          currentUser.uid,
          (properties) => {
            console.log('Owner properties received:', properties);
            setProperties(properties || []);
          }
        );

        // Subscribe to tenants
        const tenantUnsubscribe = tenantService.subscribeToTenantAssignmentsByOwner(
          currentUser.uid,
          (tenants) => {
            console.log('Owner tenants received:', tenants);
            setTenants(tenants || []);
          }
        );

        // Subscribe to tickets
        const ticketUnsubscribe = ticketService.subscribeToTickets(
          currentUser.uid,
          'owner',
          (tickets) => {
            console.log('Owner tickets received:', tickets);
            setTickets(tickets || []);
            setLoading(false);
            console.log('Loading completed, tickets count:', tickets?.length || 0);
          }
        );

        // Set a fallback timeout to prevent infinite loading
        const fallbackTimeout = setTimeout(() => {
          console.log('Fallback timeout reached, forcing loading to complete');
          setLoading(false);
        }, 10000); // 10 seconds timeout

        // Also set loading to false if no tickets are found after a reasonable delay
        const noDataTimeout = setTimeout(() => {
          console.log('No data timeout reached, checking if loading should complete');
          // If we have any data or if profile completion is needed, complete loading
          if (properties.length > 0 || tenants.length > 0 || tickets.length > 0 || showProfileCompletion) {
            console.log('Data found or profile completion needed, completing loading');
            setLoading(false);
          } else {
            console.log('Still no data, will wait for fallback timeout');
          }
        }, 3000); // 3 seconds delay

        // Show onboarding tutorial for new users (first time login) - AFTER setting up subscriptions
        if (userData.phoneNumber && userData.address && needsOnboarding()) {
          console.log('Onboarding tutorial will be shown after delay');
          // Delay showing tutorial to let user see the dashboard first
          const timer = setTimeout(() => {
            setShowOnboardingTutorial(true);
          }, 2000);
          
          // Return cleanup function that includes both tutorial timer and subscriptions
          return () => {
            clearTimeout(timer);
            clearTimeout(fallbackTimeout);
            clearTimeout(noDataTimeout);
            propertyUnsubscribe();
            tenantUnsubscribe();
            ticketUnsubscribe();
          };
        }

        console.log('No onboarding needed, setting up subscriptions only');
        // Return cleanup function for subscriptions only (when no tutorial needed)
        return () => {
          clearTimeout(fallbackTimeout);
          clearTimeout(noDataTimeout);
          propertyUnsubscribe();
          tenantUnsubscribe();
          ticketUnsubscribe();
        };
      } catch (error) {
        console.error('Error setting up subscriptions:', error);
        setTickets([]);
        setProperties([]);
        setTenants([]);
        setLoading(false);
      }
    } else {
      console.log('No currentUser or userData:', { currentUser, userData });
      setLoading(false);
    }
  }, [currentUser, userData]);

  // Listen for custom event from sidebar to show tutorial
  useEffect(() => {
    const handleShowTutorial = () => {
      setShowOnboardingTutorial(true);
    };

    window.addEventListener('showOnboardingTutorial', handleShowTutorial);
    
    return () => {
      window.removeEventListener('showOnboardingTutorial', handleShowTutorial);
    };
  }, []);

  useEffect(() => {
    const calculateTotalExpenses = async () => {
      if (tickets.length > 0) {
        let total = 0;
        for (const ticket of tickets) {
          const ticketExpenses = await expenseService.getTicketTotalExpenses(ticket.id);
          total += ticketExpenses;
        }
        setTotalExpenses(total);
      }
    };

    calculateTotalExpenses();
  }, [tickets]);



  const handleAddProperty = async (e) => {
    e.preventDefault();
    try {
      await propertyService.createProperty({
        ...newProperty,
        ownerId: currentUser.uid,
        status: 'vacant' // Default status for new properties
      });
      
      setNewProperty({ name: '', address: '', type: 'residential' });
      setShowAddProperty(false);
    } catch (error) {
      console.error('Error adding property:', error);
      alert('Failed to add property. Please try again.');
    }
  };

  const handleProfileCompletion = async (e) => {
    e.preventDefault();
    try {
      const { phoneNumber, address } = profileCompletionForm;
      
      // Validate required fields
      if (!phoneNumber.trim()) {
        alert('Phone number is required. Please enter your phone number.');
        return;
      }
      
      if (!address.trim()) {
        alert('Address is required. Please enter your address.');
        return;
      }
      
      // Update user data with phone and address
      await authService.updateUserData(currentUser.uid, {
        phoneNumber: phoneNumber.trim(),
        address: address.trim(),
        profileCompletedAt: new Date()
      });
      
      // Reset form and close modal
      setProfileCompletionForm({
        phoneNumber: phoneNumber.trim(),
        address: address.trim()
      });
      setShowProfileCompletion(false);
      
      // Refresh user data to reflect the changes
      window.location.reload();
      
      alert('Profile completed successfully! Your contact information has been saved.');
    } catch (error) {
      console.error('Error completing profile:', error);
      alert('Failed to complete profile. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'in-progress':
        return 'bg-slate-100 text-slate-800 border-slate-300';
      case 'completed':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'closed':
        return 'bg-slate-50 text-slate-500 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getPropertyStatusColor = (status) => {
    switch (status) {
      case 'occupied':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'vacant':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />

        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center space-x-3">
                <LayoutDashboard className="w-6 h-6 text-slate-600" strokeWidth={1.75} />
                <h1 className="text-xl font-semibold text-slate-800">{t('dashboard.owner.title')}</h1>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
                  title={t('feedback.submit')}
                >
                  <MessageSquare className="w-4 h-4" strokeWidth={1.75} />
                  <span className="hidden sm:inline">{t('feedback.submit')}</span>
                </button>
                <button
                  onClick={() => setShowOnboardingTutorial(true)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
                  title={t('dashboard.common.help')}
                >
                  <HelpCircle className="w-4 h-4" strokeWidth={1.75} />
                  <span className="hidden sm:inline">{t('dashboard.common.help')}</span>
                </button>
                <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
              </div>
            </div>
          </header>

          <div className="p-8">
            {needsOnboarding() && (
              <div className="mb-6 p-5 bg-white border border-slate-200 rounded-lg shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h2 className="text-base font-semibold text-slate-800 mb-1">
                      {t('dashboard.owner.onboarding.welcome')}
                    </h2>
                    <p className="text-sm text-slate-600 mb-4">
                      {t('dashboard.owner.onboarding.subtitle')}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowOnboardingTutorial(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 transition-colors"
                      >
                        <Info className="w-4 h-4" strokeWidth={1.75} />
                        {t('dashboard.owner.onboarding.startTutorial')}
                      </button>
                      <button
                        onClick={() => {
                          authService.updateUserData(currentUser.uid, { onboardingCompleted: true });
                          window.location.reload();
                        }}
                        className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                      >
                        {t('dashboard.owner.onboarding.skipForNow')}
                      </button>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Info className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-slate-800 rounded-lg p-5 text-white shadow-sm border border-slate-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-semibold uppercase tracking-wide">
                        {userData?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-300">Welcome back</p>
                      <p className="font-semibold text-slate-50">{userData?.displayName?.split(' ')[0] || 'Owner'}</p>
                    </div>
                  </div>
                  {needsOnboarding() && (
                    <button
                      onClick={() => setShowOnboardingTutorial(true)}
                      className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                      title="Get started with tutorial"
                    >
                      <HelpCircle className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">{properties.length}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">{t('dashboard.owner.stats.properties')}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">{tickets.length}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">{t('dashboard.owner.stats.tickets')}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">${totalExpenses.toFixed(2)}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">{t('dashboard.owner.stats.expenses')}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums">{tenants.length}</p>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mt-0.5">{t('dashboard.owner.stats.tenants')}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>

              <Link
                to="/providers"
                className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 flex items-center justify-between gap-3 hover:border-slate-300 hover:shadow-md transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200 transition-colors">
                    <Wrench className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{t('sidebar.serviceProviders')}</p>
                    <p className="text-xs text-slate-500 truncate">{t('dashboard.owner.stats.findProviders') || 'Find verified pros'}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 group-hover:text-slate-600 transition-colors" strokeWidth={1.75} />
              </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                    <h2 className="text-base font-semibold text-slate-800">{t('dashboard.owner.sections.properties.title')}</h2>
                  </div>
                  <Link
                    to="/properties"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
                  >
                    {t('dashboard.common.viewAll')}
                    <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                  </Link>
                </div>
                <p className="text-sm text-slate-500 mb-4">{t('dashboard.owner.sections.properties.subtitle')}</p>

                {properties.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Building2 className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-sm font-medium text-slate-800 mb-1">{t('dashboard.owner.sections.properties.noProperties')}</h3>
                    <p className="text-xs text-slate-500 mb-4">{t('dashboard.owner.sections.properties.emptyState')}</p>
                    <div className="flex items-center justify-center gap-2">
                      <Link
                        to="/properties"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 transition-colors"
                      >
                        {t('dashboard.owner.sections.properties.addFirstProperty')}
                      </Link>
                      {needsOnboarding() && (
                        <button
                          onClick={() => setShowOnboardingTutorial(true)}
                          className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                        >
                          {t('dashboard.owner.sections.properties.needHelp')}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {properties.map((property) => (
                      <div key={property.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border border-slate-100">
                        <div className="w-9 h-9 bg-slate-200 rounded-md flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{property.name}</p>
                          <p className="text-xs text-slate-500 truncate">{property.address}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border flex-shrink-0 ${getPropertyStatusColor(property.status)}`}>
                          {property.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                    <h2 className="text-base font-semibold text-slate-800">{t('dashboard.owner.sections.tenants.title')}</h2>
                  </div>
                  <button
                    onClick={() => setShowCreateTenant(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 transition-colors"
                  >
                    {t('dashboard.owner.sections.tenants.createTenant')}
                  </button>
                </div>
                <p className="text-sm text-slate-500 mb-4">{t('dashboard.owner.sections.tenants.subtitle')}</p>

                {tenants.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Users className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-sm font-medium text-slate-800 mb-1">{t('dashboard.owner.sections.tenants.noTenants')}</h3>
                    <p className="text-xs text-slate-500 mb-4">{t('dashboard.owner.sections.tenants.emptyState')}</p>
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setShowCreateTenant(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 transition-colors"
                      >
                        {t('dashboard.owner.sections.tenants.createFirstTenant')}
                      </button>
                      {needsOnboarding() && (
                        <button
                          onClick={() => setShowOnboardingTutorial(true)}
                          className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                        >
                          {t('dashboard.owner.sections.tenants.needHelp')}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {tenants.slice(0, 5).map((tenant) => (
                      <div key={tenant.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border border-slate-100">
                        <div className="w-9 h-9 bg-slate-200 rounded-md flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{tenant.tenantName}</p>
                          <p className="text-xs text-slate-500 truncate">{tenant.tenantEmail}</p>
                        </div>
                        <span className="px-2 py-0.5 text-xs font-medium rounded border bg-slate-100 text-slate-700 border-slate-200 flex-shrink-0">
                          {tenant.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                    <h3 className="text-base font-semibold text-slate-800">{t('dashboard.owner.sections.maintenance.title')}</h3>
                  </div>
                  <Link
                    to="/tickets"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors"
                  >
                    {t('dashboard.common.viewAll')}
                    <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
                  </Link>
                </div>
                <p className="text-sm text-slate-500 mb-4">{t('dashboard.owner.sections.maintenance.subtitle')}</p>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {tickets.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Ticket className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-sm font-medium text-slate-800 mb-1">{t('dashboard.owner.sections.maintenance.noRequests')}</h3>
                      <p className="text-xs text-slate-500 mb-4">{t('dashboard.owner.sections.maintenance.emptyState')}</p>
                      {needsOnboarding() && (
                        <button
                          onClick={() => setShowOnboardingTutorial(true)}
                          className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                        >
                          {t('dashboard.owner.sections.maintenance.learnHow')}
                        </button>
                      )}
                    </div>
                  ) : (
                    tickets.slice(0, 5).map((ticket) => (
                      <div key={ticket.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-md border border-slate-100">
                        <div className="w-9 h-9 bg-slate-200 rounded-md flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/tickets/${ticket.id}`}
                            className="text-sm font-medium text-slate-800 hover:text-slate-600 transition-colors truncate block"
                          >
                            {ticket.title || t('dashboard.owner.sections.maintenance.requestTitle')}
                          </Link>
                          <p className="text-xs text-slate-500 truncate">{ticket.propertyName || t('dashboard.owner.sections.maintenance.property')}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border flex-shrink-0 ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Tenant Account Modal */}
      <CreateTenantAccountModal
        isOpen={showCreateTenant}
        onClose={() => setShowCreateTenant(false)}
        properties={properties}
        onTenantCreated={(result) => {
          console.log('Tenant created:', result);
          // The modal will handle showing the success message
        }}
      />

      {showAddProperty && (
        <div className="fixed inset-0 bg-slate-900/60 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-16 pb-8">
          <div className="w-full max-w-md mx-4 bg-white border border-slate-200 rounded-lg shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-800">Add New Property</h3>
              <button
                onClick={() => setShowAddProperty(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
            <form onSubmit={handleAddProperty} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Property Name</label>
                <input
                  type="text"
                  required
                  value={newProperty.name}
                  onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 text-sm"
                  placeholder="Enter property name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Address</label>
                <input
                  type="text"
                  required
                  value={newProperty.address}
                  onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 text-sm"
                  placeholder="Enter property address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Property Type</label>
                <select
                  value={newProperty.type}
                  onChange={(e) => setNewProperty({...newProperty, type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 text-sm"
                >
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddProperty(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 transition-colors"
                >
                  Add Property
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showProfileCompletion && (
        <div className="fixed inset-0 bg-slate-900/60 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-16 pb-8">
          <div className="w-full max-w-md mx-4 bg-white border border-slate-200 rounded-lg shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-800">Complete Your Profile</h3>
              <button
                onClick={() => setShowProfileCompletion(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
            <div className="px-5 pt-4">
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                  <p className="text-sm text-slate-600">
                    Please provide your contact information. This helps us provide better service.
                  </p>
                </div>
              </div>
              <form onSubmit={handleProfileCompletion} className="space-y-4 pb-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={profileCompletionForm.phoneNumber}
                    onChange={(e) => setProfileCompletionForm({...profileCompletionForm, phoneNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 text-sm"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Address *</label>
                  <textarea
                    required
                    rows={3}
                    value={profileCompletionForm.address}
                    onChange={(e) => setProfileCompletionForm({...profileCompletionForm, address: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 text-sm"
                    placeholder="Enter your full address (street, city, state, zip code)"
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-slate-700 rounded-md hover:bg-slate-800 transition-colors"
                  >
                    Save Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

             {/* Onboarding Tutorial Modal */}
       {showOnboardingTutorial && (
         <OnboardingTutorial
           isOpen={showOnboardingTutorial}
           onClose={() => setShowOnboardingTutorial(false)}
           onComplete={() => {
             authService.updateUserData(currentUser.uid, { onboardingCompleted: true });
             setShowOnboardingTutorial(false);
             alert('Onboarding tutorial completed! You can now start managing your properties.');
           }}
         />
       )}

       {/* Feedback Modal */}
       <FeedbackModal
         isOpen={showFeedbackModal}
         onClose={() => setShowFeedbackModal(false)}
       />
     </div>
   );
 };

export default OwnerDashboard; 